// src/utils/registerMovement.js
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const DEFAULTS = {
    categoria: 'General',
    tipoMovimiento: 'movimiento',
    origen: 'sistema',
};

const RESERVED_METADATA_KEYS = new Set([
    'fraccionamientoId',
    'titulo',
    'descripcion',
    'categoria',
    'tipoMovimiento',
    'origen',
    'fecha',
    'actorUid',
    'actorNombre',
    'actorEmail',
    'actorRol',
    'actorRolGrupo',
]);

const normalizeString = (value, fallback = '') => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : fallback;
    }
    return fallback;
};

const sanitizeMetadata = (metadata) => {
    if (!metadata || typeof metadata !== 'object') {
        return undefined;
    }

    const sanitized = {};

    Object.entries(metadata).forEach(([key, value]) => {
        if (!key || RESERVED_METADATA_KEYS.has(key)) {
            return;
        }

        if (value === undefined) {
            return;
        }

        if (value === null) {
            sanitized[key] = null;
            return;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed.length > 0) {
                sanitized[key] = trimmed;
            }
            return;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            sanitized[key] = value;
            return;
        }

        if (value instanceof Date && !Number.isNaN(value.valueOf())) {
            sanitized[key] = value.toISOString();
            return;
        }

        try {
            const candidate = JSON.parse(JSON.stringify(value));
            sanitized[key] = candidate;
        } catch (serializationError) {
            // Ignoramos valores no serializables
        }
    });

    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};

const buildMovementRequest = ({
    fraccionamientoId,
    titulo,
    descripcion,
    categoria,
    tipoMovimiento,
    origen,
    metadata,
}) => {
    const payload = {
        fraccionamientoId,
        titulo: normalizeString(titulo, 'Movimiento registrado'),
        descripcion: normalizeString(descripcion, ''),
        categoria: normalizeString(categoria, DEFAULTS.categoria),
        tipoMovimiento: normalizeString(tipoMovimiento, DEFAULTS.tipoMovimiento),
        origen: normalizeString(origen, DEFAULTS.origen),
    };

    const sanitizedMetadata = sanitizeMetadata(metadata);
    if (sanitizedMetadata) {
        payload.metadata = sanitizedMetadata;
    }

    return payload;
};

let cachedCallable = null;

const getCallable = () => {
    if (!cachedCallable) {
        cachedCallable = httpsCallable(functions, 'logSystemMovement');
    }
    return cachedCallable;
};

/**
 * Registra un movimiento en la colección de notificaciones del fraccionamiento.
 */
export const registerMovement = async ({
    fraccionamientoId,
    titulo,
    descripcion,
    categoria,
    tipoMovimiento,
    origen,
    metadata,
}) => {
    if (!fraccionamientoId) {
        throw new Error('registerMovement: fraccionamientoId es requerido');
    }

    const payload = buildMovementRequest({
        fraccionamientoId,
        titulo,
        descripcion,
        categoria,
        tipoMovimiento,
        origen,
        metadata,
    });

    const callable = getCallable();
    const response = await callable(payload);
    const data = response?.data || {};

    if (data && Object.prototype.hasOwnProperty.call(data, 'success') && data.success === false) {
        const message = typeof data.message === 'string' && data.message.trim().length > 0
            ? data.message
            : 'No se pudo registrar el movimiento.';
        throw new Error(message);
    }

    return data;
};
